/**
 * 1stbuddha — toc.js
 *
 * <toc-nav data-source="#ArticleBody"> builds an "In this article" / "Chapters"
 * list from the H2s inside data-source:
 *   - gives each H2 an id (from its text) when it has none
 *   - fills [data-toc-list] with links; the host stays hidden when there are
 *     fewer than two headings
 *   - the link of the section being read gets aria-current (scroll-spy)
 *   - inside a <details> (mobile), choosing a link closes it
 * Several <toc-nav> elements can share one source (desktop aside + mobile).
 */
(() => {
  if (customElements.get('toc-nav')) return;

  const slug = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';

  const ensureIds = (headings) => {
    headings.forEach((heading) => {
      if (heading.id) return;
      const base = slug(heading.textContent.trim());
      let id = base;
      let n = 2;
      while (document.getElementById(id)) id = `${base}-${n++}`;
      heading.id = id;
    });
  };

  class TocNav extends HTMLElement {
    connectedCallback() {
      const source = document.querySelector(this.dataset.source);
      const list = this.querySelector('[data-toc-list]');
      if (!source || !list) return;
      const headings = [...source.querySelectorAll('h2')].filter((h) => h.textContent.trim());
      if (headings.length < 2) return;
      ensureIds(headings);

      this.links = headings.map((heading) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
        return link;
      });
      this.hidden = false;

      const details = this.querySelector('details');
      list.addEventListener('click', (event) => {
        if (event.target.closest('a') && details) details.open = false;
      });

      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) this.setCurrent(visible[0].target.id);
        },
        { rootMargin: '-15% 0px -70% 0px' }
      );
      headings.forEach((heading) => this.observer.observe(heading));
      this.setCurrent(headings[0].id);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    setCurrent(id) {
      this.links?.forEach((link) => {
        if (link.getAttribute('href') === `#${id}`) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('toc-nav', TocNav);
})();
